import { z } from 'zod';

/**
 * Typed, validated accessor over `import.meta.env`.
 *
 * Vite exposes environment variables as `string | undefined` with no validation at all, so a
 * typo in a `.env` file surfaces as `undefined` deep inside a component at runtime. Parsing
 * the whole set once, here, turns that into a single explicit failure at startup naming the
 * variable — which is the difference between a five-second fix and an afternoon.
 */
const envSchema = z.object({
  /** Empty means "same origin". The app calls relative /api/v1/... in every environment. */
  VITE_API_BASE_URL: z.string().default(''),

  VITE_ENVIRONMENT: z.enum(['local', 'development', 'staging', 'production']).default('local'),

  VITE_REQUEST_TIMEOUT_MS: z.coerce.number().int().positive().default(30_000),

  /**
   * Cloudflare Turnstile site key (public). Empty hides the widget; set it wherever the backend
   * has captcha.enabled=true. 1x00000000000000000000AA is Cloudflare's always-pass test key.
   */
  VITE_TURNSTILE_SITE_KEY: z.string().default(''),
});

function parseEnv() {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    const problems = result.error.issues
      .map((issue) => `  ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');
    throw new Error(`Invalid environment configuration:\n${problems}`);
  }

  return result.data;
}

export const env = parseEnv();

export type Env = typeof env;
